import './shadcn-theme.css';
import Footer from '@/components/Footer';
import { SelectLang, NoticeIcon, SelectSystem, AddTask } from '@/components/RightContent';
import { LinkOutlined, QuestionCircleOutlined, SmileOutlined } from '@ant-design/icons';
import { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import {
  currentUser as queryCurrentUser,
  getTokenInfo,
  getUserSystemCache,
  listSchedulingUser,
  listChangeSystem,
} from '@/services/common/api';
import { redirectToSSO } from '@/utils/sso';
import React, { Component, useEffect } from 'react';
import { AvatarDropdown, AvatarName } from './components/RightContent/AvatarDropdown';
import logo from './logo.png';
import { ConfigProvider, Row, Col, Tag, Typography, Button, Result, Tooltip, Alert } from 'antd';

import IssueSearch from './components/IssueSearch';
import FindPeople from './components/FindPeople';
import Notifications from '@/components/NoticeIcon/Notifications';
import {
  selectBusinessList,
  selectSystemDetails,
  userParticipateBusiness,
} from './services/rd/system/api';
import OptimizedMarquee from './components/OptimizedMarquee';

const isDev = REACT_APP_ENV === 'dev';

// 不需要登录的页面路由
const noLoginPath = ['/questionnaire'];

// 获取页面路由
const hashPath = window.location.hash; // 获取 hash 部分
const pathname = hashPath.replace('#', '').split('?')[0]; // 提取 页面路由

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  roles?: any[];
  systemList: any[];
  permissions?: [];
  schedulingUser?: object;
  loading?: boolean;
  fetchUserInfo?: () => Promise<any | undefined>;
  defaultSystem: any;
  isBusinessLeader?: boolean;
  isMainProductManager?: boolean;
  isBusinessParticipant?: boolean;
  isSystemInterfaceManager?: boolean;
}> {
  const fetchUserInfo = async () => {
    const { location } = history;
    const accessToken = localStorage.getItem('accessToken');

    // 1. 如果没有 accessToken，检查是否是 SSO 回调
    if (!accessToken) {
      // 获取 URL 参数（SSO 回调会带参数）
      const searchParams = new URLSearchParams(location.search);
      const code = searchParams.get('code');

      // 如果没有回调参数，说明不是登录回调，直接跳转到 SSO
      if (!code) {
        // 不在免登录页面时，才跳转 SSO
        if (!(noLoginPath.indexOf(pathname) > -1)) {
          redirectToSSO();
        }
      }

      // 是 SSO 回调，调用 getTokenInfo 获取 token
      try {
        // 将查询字符串转换为对象，以便以 JSON 格式发送
        const searchParams = new URLSearchParams(location.search);
        const params: Record<string, string> = {};
        searchParams.forEach((value, key) => {
          params[key] = value;
        });
        const response = await getTokenInfo(params);
        const resultData = response?.resultObject || response;
        const newAccessToken = resultData?.accessToken;
        const newRefreshToken = resultData?.refreshToken;

        if (!newAccessToken) {
          console.error('获取 accessToken 失败，响应数据：', response);
        }

        // 存储 token 到 localStorage
        localStorage.setItem('accessToken', newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        // 登录成功，跳转到首页，会重新触发 getInitialState 获取用户信息
        history.push('/');
      } catch (error) {
        console.error('SSO 回调处理失败：', error);
      }
    }

    // 2. 有 accessToken，获取用户信息
    try {
      const userInfo = await queryCurrentUser({
        skipErrorHandler: true,
      }) as any;

      // 探针：只在 test/prod 环境存储 wmUserInfo（用于监控）
      if ((REACT_APP_ENV === 'test' && !isDev) || REACT_APP_ENV === 'prod') {
        const env = REACT_APP_ENV === 'prod' ? 'pre' : 'dev';
        window.localStorage.wmUserInfo = JSON.stringify({
          userId: userInfo?.user?.userName,
          userTag: 'tag',
          projectVersion: '1.0.1',
          env: env,
        });
      }

      // 存储 userCode 到 localStorage，用于刷新 token
      if (userInfo?.user?.userName) {
        localStorage.setItem('userCode', userInfo.user.userName);
      }

      // 用户信息存储在 initialState 中（通过 return userInfo）
      return userInfo;
    } catch (error) {
      // 获取用户信息失败，返回 undefined（401 会由响应拦截器处理）
      return undefined;
    }
  };

  const getDefaultSystem = async () => {
    try {
      const systemId = await getUserSystemCache({
        skipErrorHandler: true,
      });
      return systemId;
    } catch (error) {
      console.log(error);
    }
    return undefined;
  };

  const listSystems = async () => {
    try {
      const systems = await listChangeSystem({
        skipErrorHandler: true,
      });
      return systems;
    } catch (error) {
      console.log(error);
    }
    return undefined;
  };

  // 如果是免登录页面，直接返回
  if (noLoginPath.indexOf(pathname) > -1) {
    return {
      currentUser: undefined,
      defaultSystem: '',
      loading: false,
      permissions: [],
      roles: [],
      schedulingUser: undefined,
      fetchUserInfo,
      systemList: [],
      isBusinessLeader: false,
      isMainProductManager: false,
      isBusinessParticipant: false,
      isSystemInterfaceManager: false,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // 不是免登录页面，必须先登录
  const userInfo = await fetchUserInfo() as any;

  // 如果获取用户信息失败（未登录），返回空的 initialState（layout 会处理跳转）
  if (!userInfo || !userInfo?.user) {
    return {
      fetchUserInfo,
      currentUser: undefined,
      defaultSystem: '',
      loading: false,
      permissions: [],
      roles: [],
      schedulingUser: undefined,
      systemList: [],
      isBusinessLeader: false,
      isMainProductManager: false,
      isBusinessParticipant: false,
      isSystemInterfaceManager: false,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  // 已登录，获取其他业务数据
  const currentUser = {
    ...(userInfo.user || {}),
    userInfo: userInfo.userInfo,
  };
  const date = new Date();
  const [
    systemId,
    scheduling,
    systems,
    systemDetails,
    businessLeaderList,
    userParticipateBusinessList,
  ] = await Promise.all([
    getDefaultSystem(),
    listSchedulingUser({ date: date.getDay() }),
    listSystems(),
    selectSystemDetails().then((res) => res?.resultObject || res || []),
    selectBusinessList().then((res) => res?.resultObject || []),
    userParticipateBusiness(currentUser?.userName).then((res) => res?.resultObject || []),
  ]);

  const roles = userInfo?.roles || [];
  const permissions = userInfo?.permissions || [];
  const schedulingUser = scheduling?.resultObject?.[0]?.onDutyDTO;
  const defaultSystem = systemId?.resultObject || '';
  const interfaceManagerCodesBySystemId = new Map(
    (Array.isArray(systemDetails) ? systemDetails : []).map((item: any) => [
      String(item?.systemId ?? ''),
      Array.isArray(item?.interfaceManagerCodes) ? item.interfaceManagerCodes : [],
    ]),
  );
  const systemList = (systems?.resultObject || []).map((item: any) => ({
    ...item,
    interfaceManagerCodes:
      interfaceManagerCodesBySystemId.get(String(item?.systemId ?? '')) ||
      (Array.isArray(item?.interfaceManagerCodes) ? item.interfaceManagerCodes : []),
  }));
  const settings = defaultSettings as Partial<LayoutSettings>;

  // 是否是业务领域负责人
  const isBusinessLeader = businessLeaderList?.some(
    (item: any) => item.businessLeaderUserCode === currentUser?.userName,
  );

  const isMainProductManager = businessLeaderList?.some(
    (item: any) =>
      Array.isArray(item?.mainProductManagerInfo) &&
      item.mainProductManagerInfo.includes(currentUser?.userName),
  );

  // 是否是业务领域参与者
  const isBusinessParticipant = userParticipateBusinessList?.length > 0;

  // 是否是任一系统的研发接口人
  const isSystemInterfaceManager = systemList?.some(
    (item: any) =>
      Array.isArray(item?.interfaceManagerCodes) &&
      item.interfaceManagerCodes.includes(currentUser?.userName),
  );

  const navTheme = localStorage.getItem('navTheme');
  if (navTheme && (navTheme === 'realDark' || navTheme === 'light')) {
    settings.navTheme = navTheme;
  }

  return {
    fetchUserInfo,
    currentUser,
    roles,
    permissions,
    schedulingUser,
    defaultSystem,
    systemList,
    isBusinessLeader,
    isMainProductManager,
    isBusinessParticipant,
    isSystemInterfaceManager,
    settings: settings || (defaultSettings as Partial<LayoutSettings>),
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layouthttps://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  // 免登录页面：只加载内容，无需加载菜单等
  if (noLoginPath.indexOf(pathname) > -1) {
    return {
      pure: true,
      colorPrimary: '#00c479',
    };
  } else {
    // // 需要登录的页面：检查是否已登录
    // if (!initialState?.currentUser) {
    //   const accessToken = localStorage.getItem('accessToken');
    //   if (!accessToken) {
    //     redirectToSSO();
    // }

    // 已登录，返回完整布局
    return {
      actionsRender: () => {
        // 横向 flex：跑马灯区可收缩（minWidth:0），右侧搜索/系统/通知等不被挤出视口
        return [
          <div
            key="header-actions"
            className="app-header-actions"
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
              flex: 1,
              gap: 12,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                flex: '1 1 0%',
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Alert
                banner
                style={{
                  margin: 0,
                  cursor: 'default',
                }}
                message={<OptimizedMarquee />}
              />
            </div>
            <div
              className="app-header-toolbar"
              style={{
                display: 'flex',
                alignItems: 'center',
                flex: '0 1 auto',
                minWidth: 0,
                gap: 8,
              }}
            >
              <IssueSearch key="IssueSearch" />
              <AddTask key="addTask" />
              <SelectSystem key="SelectSystem" />
              <NoticeIcon key="NoticeIcon" />
              <Tooltip title="使用文档">
                <span
                  key="help"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 32,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    window.open(`${API_SERVER}/help`);
                  }}
                >
                  <QuestionCircleOutlined style={{ color: '#00BC73', fontSize: 16 }} />
                </span>
              </Tooltip>
            </div>
          </div>,
        ];
      },

      avatarProps: {
        src: initialState?.currentUser?.avatar,
        title: (
          <span
            style={{
              maxWidth: 'none',
              whiteSpace: 'nowrap',
              overflow: 'visible',
              textOverflow: 'clip',
            }}
          >
            <AvatarName />
          </span>
        ),
        render: (_: any, avatarChildren: any) => {
          return <AvatarDropdown menu={false}>{avatarChildren}</AvatarDropdown>;
        },
      },
      siderWidth: 218,
      defaultCollapsed: true,
      breakpoint: false,
      // title: <Space><h1>研发效能平台</h1><Input.Search style={{ marginLeft: 70 }} bordered={false} placeholder="input search text" onSearch={() => { console.log(11111) }} enterButton /></Space>,
      onCollapse: (collapsed: boolean) => {
        setInitialState((preInitialState: any) => ({
          ...preInitialState,
          settings: {
            ...(preInitialState?.settings || {}),
            collapsed,
          },
        }));
      },
      headerTitleRender: (logo: any, title: any, _: any) => {
        return (
          <a>
            {logo}
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'visible',
                fontSize: '16px', // 可以调整字体大小以适应空间
              }}
            >
              {title}
            </span>
          </a>
        );
      },
      waterMarkProps: {
        fontSize: 13,
        content: [
          initialState?.currentUser?.nickName || '',
          new Date().toString().replace(/\s*$.*?$$/, ''),
        ].filter(Boolean),
        markStyle: { opacity: 0.4 },
      },
      logo: logo,
      // footerRender: () => <Footer/>,
      onPageChange: () => {
        const currentPath = history.location.pathname;
        // 如果当前路径不是免登录页面，且未登录，跳转到 SSO
        if (noLoginPath.indexOf(currentPath) === -1 && !initialState?.currentUser) {
          const accessToken = localStorage.getItem('accessToken');
          if (!accessToken) {
            redirectToSSO();
          }
        }
      },
      bgLayoutImgList: [
        {
          src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
          left: 85,
          bottom: 100,
          height: '303px',
        },
        {
          src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
          bottom: -68,
          right: -45,
          height: '303px',
        },
        {
          src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
          bottom: 0,
          left: 0,
          width: '331px',
        },
      ],
      links: isDev
        ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
        : [],
      menuHeaderRender: undefined,
      // menuDataRender: (menuData) => {
      //   return menuData.map(item => {
      //     if (item.name === 'NextChat') {
      //       item.icon = <><SmileOutlined style={{ marginRight: 9 }} />{item.name}<Tag color='red' style={{ width: 25, height: 15, fontSize: 7, lineHeight: '14px', paddingInline: 3, marginInlineStart: 3 }}>NEW</Tag></>
      //     } return item
      //   })
      // },
      menuDataRender: (menuData: any) => {
        const isCollapsed = (initialState?.settings as any)?.collapsed;
        const nameMapping: Record<string, string> = {
          Dashboard: '概览',
          工作台: '工作台',
          项目管理: '项目',
          空间管理: '空间列表',
          项目记录: '项目列表',
          研发协同: '研发',
          反馈管理: '反馈',
          发布看板: '发布',
          效能洞察: '效能',
          Tools: '工具',
          AiCoding: 'Coding',
          审批: '审批',
          CMDB: 'CMDB',
          技术管理: '管理',
          subscribe: '订阅',
          设置: '设置',
          可观测平台: '可观测',
        };
        return menuData.map((item: any) => {
          if (isCollapsed === undefined || isCollapsed === true) {
            return {
              ...item,
              icon: (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginBottom: '20px',
                    transform:
                      item.name === 'CMDB'
                        ? 'translate(-20%, 40%)'
                        : item.name === 'techManagement'
                          ? 'translate(-40%, 32%)'
                          : 'translate(-15%, 32%)', //左、下调整
                  }}
                >
                  {item.icon}
                  <div style={{ fontSize: '10px', color: '#888' }}>
                    {nameMapping[item.name || ''] || item.name}
                  </div>
                </div>
              ),
            };
          }
          return item;
        });
      },
      // 自定义 403 页面
      // unAccessible: <div>unAccessible</div>,
      // 增加一个 loading 的状态
      childrenRender: (children: any) => {
        // if (initialState?.loading) return <PageLoading />;
        return (
          <ConfigProvider
            componentSize={'small'}
            theme={{
              token: {
                fontSize: 12,
              },
            }}
          >
            <ErrorBoundary>{children}</ErrorBoundary>
            <FindPeople />
            <Notifications />
            {isDev && (
              <SettingDrawer
                disableUrlParams
                enableDarkTheme
                settings={initialState?.settings}
                onSettingChange={(settings: any) => {
                  setInitialState((preInitialState: any) => ({
                    ...preInitialState,
                    settings,
                  }));
                }}
              />
            )}
          </ConfigProvider>
        );
      },
      ...initialState?.settings,
      // 菜单支持拖拽
      // menuExtraRender: () => {
      //   return (
      //     <div
      //       style={{
      //         position: 'absolute',
      //         right: -5,
      //         top: 0,
      //         bottom: 0,
      //         width: 10,
      //         cursor: 'ew-resize',
      //       }}
      //       onMouseDown={(e) => {
      //         e.preventDefault();
      //         const startX = e.clientX;

      //         function handleMouseMove(event: MouseEvent) {
      //           const diff = event.clientX - startX;
      //           // 根据拖拽方向设置折叠状态
      //           setInitialState((prev: any) => ({
      //             ...prev,
      //             settings: {
      //               ...prev.settings,
      //               collapsed: diff < 0, // 往左拖就折叠，往右保持展开
      //             },
      //           }));
      //         }

      //         function handleMouseUp() {
      //           document.removeEventListener('mousemove', handleMouseMove);
      //           document.removeEventListener('mouseup', handleMouseUp);
      //         }

      //         document.addEventListener('mousemove', handleMouseMove);
      //         document.addEventListener('mouseup', handleMouseUp);
      //       }}
      //     />
      //   );
      // },
    };
  }


};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  formatResult: (result: any) => {
    return result?.resultObject;
  },
  ...errorConfig,
};

interface ErrorBoundaryState {
  hasError: boolean;
  error: any;
  errorInfo: any;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  // 当子组件抛出错误时，这个生命周期方法会被调用
  static getDerivedStateFromError(error: any) {
    // 更新state，以便下次渲染时可以显示备用UI
    return { hasError: true };
  }

  // 此生命周期方法在子组件抛出错误后被调用
  componentDidCatch(error: any, errorInfo: any) {
    // 你可以在这里处理错误，例如记录错误信息
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    // 例如，发送错误报告到你的服务端
    // logErrorToMyService(error, errorInfo);
  }

  render() {
    if (this.state?.hasError) {
      console.log(this.state);
      // 自定义备用UI
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh', // 设置父容器高度为视口高度
          }}
        >
          <Result
            title="The page has been updated, please refresh."
            subTitle="If the refresh doesn't work, please click the '?' in the bottom right corner to provide feedback."
            extra={
              <Button type="primary" onClick={() => window.location.reload()}>
                Refresh
              </Button>
            }
          />
        </div>
      );
    }

    // 如果没有错误，正常渲染子组件
    return this.props.children;
  }
}
